import { Radio, RadioGroup } from '@headlessui/react';
import { useEffect, useMemo, useState } from 'react';
import { shopVar } from '@/lib/shopStyles';
import { useLanguage } from '@/providers/LanguageProvider';
import { PaymentMethod } from '@/lib/TicketApi';
import { Money } from '@/lib/money';
import { calculateFee } from '@/lib/calculateFee';
import * as m from '@/paraglide/messages.js';

// Below this count, mobile collapses the rest behind a "more" toggle. On desktop the grid simply
// wraps (4 per row), so all methods stay visible across two lines.
const MOBILE_VISIBLE = 4;

interface SelectPaymentMethodProps {
    paymentMethods: PaymentMethod[];
    selectedMethod: string|null;
    onMethodChange: (method: string) => void;
    className?: string;
    additionalFee?: Money;
    totalAmount?: Money; // Add totalAmount prop for percentage fee calculation
}

export function SelectPaymentMethod({ paymentMethods, selectedMethod, onMethodChange, className, additionalFee, totalAmount }: SelectPaymentMethodProps) {
    const { currentLanguage: lang } = useLanguage();
    const [expanded, setExpanded] = useState(false);

    // Get default currency from additional fee or fallback to EUR
    const defaultCurrency = totalAmount?.currency || additionalFee?.currency || 'EUR';

    // Hide methods whose order-amount window (e.g. in3: €50–€5,000) excludes this order. The
    // PSP would reject such orders anyway; comparing against total + the method's own fee.
    const availableMethods = useMemo(() => paymentMethods.filter((method) => {
        if (method.minOrderAmount == null && method.maxOrderAmount == null) {
            return true;
        }
        if (!totalAmount) {
            return true;
        }
        const finalTotal = totalAmount.add(calculateFee(method.fee, totalAmount, 1));
        if (method.minOrderAmount != null && finalTotal.amount < method.minOrderAmount) {
            return false;
        }
        if (method.maxOrderAmount != null && finalTotal.amount > method.maxOrderAmount) {
            return false;
        }
        return true;
    }), [paymentMethods, totalAmount]);

    // Keep the selection valid: if the currently selected method is no longer available (e.g. the
    // cart total changed and it fell outside its min/max order-amount window), it disappears from
    // the list but `selectedMethod` would otherwise keep pointing at it — letting checkout submit a
    // method the buyer can't see / didn't re-pick. Reconcile to the first available method instead.
    useEffect(() => {
        if (selectedMethod === null || availableMethods.length === 0) {
            return;
        }
        if (!availableMethods.some((method) => method.id === selectedMethod)) {
            onMethodChange(availableMethods[0].id);
        }
    }, [availableMethods, selectedMethod, onMethodChange]);

    // Total fee shown per method (its own fee plus any order-level additional fee).
    const totalFeeFor = (method: PaymentMethod): Money => {
        const methodFee = totalAmount
            ? calculateFee(method.fee, totalAmount, 1)
            : (method.fee.amountPerUnit
                ? Money.fromInteger(method.fee.amountPerUnit.amount, method.fee.amountPerUnit.currency)
                : Money.zero(defaultCurrency));
        return additionalFee && methodFee.currency === additionalFee.currency
            ? methodFee.add(additionalFee)
            : methodFee;
    };

    const hiddenOnMobile = availableMethods.slice(MOBILE_VISIBLE);
    const canCollapse = hiddenOnMobile.length > 0;

    return (
        <div className={className}>
            <RadioGroup value={selectedMethod} onChange={onMethodChange} aria-label="Payment method" className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {availableMethods.map((method, index) => {
                    const totalFee = totalFeeFor(method);
                    const selected = selectedMethod === method.id;
                    // Collapsed mobile shows only the first MOBILE_VISIBLE; desktop always shows all.
                    const hideOnMobile = canCollapse && !expanded && index >= MOBILE_VISIBLE;

                    return (
                        <Radio
                            key={method.id}
                            value={method.id}
                            className={`${hideOnMobile ? 'hidden sm:flex' : 'flex'} h-full flex-col items-center justify-center gap-2 rounded-lg border p-3 text-center hover:cursor-pointer`}
                            style={{
                                borderColor: selected ? shopVar('--shop-ctaButtonColor') : shopVar('--shop-borderColor'),
                                borderWidth: selected ? 2 : 1,
                                backgroundColor: shopVar('--shop-panelBackgroundColor'),
                                color: shopVar('--shop-textColor'),
                            }}
                        >
                            <img src={method.image} alt={method.name} className="h-8 w-auto object-contain" />
                            <span className="text-sm font-semibold leading-tight">{method.name}</span>
                            {!totalFee.isZero() && <span className="text-xs tracking-tight opacity-80">{totalFee.display(lang)}</span>}
                        </Radio>
                    );
                })}
            </RadioGroup>

            {canCollapse && (
                <button
                    type="button"
                    onClick={() => setExpanded((value) => !value)}
                    className="mt-3 flex w-full items-center justify-center gap-2 text-sm font-medium sm:hidden"
                    style={{ color: shopVar('--shop-textColor') }}
                >
                    {expanded ? (
                        <span>{m.payment_method_show_less()}</span>
                    ) : (
                        <>
                            <span>{m.payment_method_show_more()}</span>
                            <span className="flex items-center gap-1">
                                {hiddenOnMobile.map((method) => (
                                    <img key={method.id} src={method.image} alt={method.name} className="h-4 w-auto object-contain" />
                                ))}
                            </span>
                        </>
                    )}
                </button>
            )}
        </div>
    );
}
